<script setup lang="ts">
import { inject, onMounted, type Component } from 'vue';
import {
  CdrText, CdrLink, IconCaretRight, IconShoppingBag, IconStar100,
} from '@rei/cedar';

import { useAnalytics } from '../../../common/composables/useAnalytics';
import { useTags } from '../../../common/composables/useTags';
import IconReward from '../../../common/resources/icons/IconReward.vue';
import IconDocument from '../../../common/resources/icons/IconDocument.vue';

/** Card showing the member's available rewards balance in the "Help for you" carousel. */
const props = withDefaults(defineProps<{
  amount: string;
  pageName?: string;
}>(), {
  pageName: undefined,
});

const store: any = inject('store');

interface HelpLink {
  key: string;
  label: string;
  href: string;
  icon: Component;
  tag: string;
}

interface RelatedHelpLink {
  key: string;
  label: string;
  href: string;
  tag: string;
}

const helpLinks: HelpLink[] = [
  {
    key: 'activity',
    label: 'View reward activity',
    href: '/account/member-rewards#',
    icon: IconReward,
    tag: 'ViewRewardActivity',
  },
  {
    key: 'use',
    label: 'How to use rewards',
    href: '/help?a=How-do-I-apply-my-Co-op-Member-Rewards?---id--IY0fd53SSd-ZWnQxrP2SAQ',
    icon: IconShoppingBag,
    tag: 'HowToUseRewards',
  },
  {
    key: 'expiration',
    label: 'Expiration',
    href: '/help?a=When-do-my-Total-REI-Rewards-expire?---id--YcH5BqDdRAmRUiFLQoxh4Q',
    icon: IconDocument,
    tag: 'Expiration',
  },
];

const relatedHelpLinks: RelatedHelpLink[] = [
  {
    key: 'missing-rewards',
    label: "I don't see rewards for some products",
    href: '/help?a=I-earned-rewards-for-some-products-but-not-others.-Why-not?---id--_y_0QsyRQg2D_pJUHT_jIQ',
    tag: 'MissingRewards',
  },
  {
    key: 'earn-rewards',
    label: 'How do I earn rewards?',
    href: '/help?a=How-do-I-earn-a-Co-op-Member-Reward?---id--apT8JbKWQtG5mFZwOzpjsw',
    tag: 'HowDoIEarnRewards',
  },
  {
    key: 'cash-check',
    label: 'Receive rewards as cash/check?',
    href: '/help?a=Can-I-receive-my-Total-REI-Rewards-by-cash-or-check?---id--Ytf6cnCgSFiOXMxvK76dFg',
    tag: 'ReceiveRewardsAsCashCheck',
  },
];

const baseTag = useTags('getTag', 'HELP_CENTER_HELPCARD_REWARDS');

const handleLinkClick = (suffix: string) => {
  const page = props.pageName
    ? { pageName: props.pageName }
    : store?.getValues('pageName');

  useAnalytics('sendClickAnalytics', {
    ...page,
    linkName: `${baseTag}_${suffix}`,
  });
};

/**
* View tag for this card, alongside the section-level one the carousel fires.
*
* Sent on render rather than on entering the viewport: a card can sit off
* screen inside the carousel, and what this measures is how often the card was
* served, not how far the customer scrolled.
*/
onMounted(() => {
  useAnalytics('sendViewAnalytics', {
    pageName: `${baseTag}_visible`,
  });
});
</script>

<template>
  <article
    class="help-card help-card--rewards rewards-card"
    data-ui="rewards-card"
  >
    <div class="help-card__body">
      <span class="help-card__chip help-card__chip--rewards">Rewards available</span>

      <div class="rewards-card__title-row">
        <span
          class="rewards-card__badge-icon"
          aria-hidden="true"
        >
          <IconStar100 inherit-color />
        </span>

        <CdrText
          tag="h3"
          class="rewards-card__heading"
        >
          Rewards available
        </CdrText>
      </div>

      <CdrText
        tag="p"
        class="rewards-card__amount"
      >
        <span class="rewards-card__amount-value">{{ amount }}</span>
        <span class="rewards-card__amount-label">in available rewards.</span>
      </CdrText>

      <CdrText
        tag="p"
        class="rewards-card__intro"
      >
        Use your rewards online, in the REI app, or in stores.
      </CdrText>
    </div>

    <div class="help-card__section">
      <CdrText
        tag="h4"
        class="help-card__section-title"
      >
        Helpful actions
      </CdrText>

      <ul class="help-card__action-list">
        <li
          v-for="link in helpLinks"
          :key="link.key"
          class="help-card__action"
        >
          <CdrLink
            :href="link.href"
            rel="noopener noreferrer"
            class="help-card__action-link"
            :data-ui="`rewards-card-${link.key}`"
            @click="handleLinkClick(link.tag)"
          >
            <component
              :is="link.icon"
              class="help-card__action-icon"
              inherit-color
            />

            <span class="help-card__action-label">{{ link.label }}</span>

            <IconCaretRight
              class="help-card__action-caret"
              inherit-color
            />
          </CdrLink>
        </li>
      </ul>
    </div>

    <div class="help-card__section help-card__section--footer">
      <CdrText
        tag="h4"
        class="help-card__section-title"
      >
        Related help
      </CdrText>

      <ul class="rewards-card__help-list">
        <li
          v-for="link in relatedHelpLinks"
          :key="link.key"
          class="rewards-card__help-item"
        >
          <CdrLink
            :href="link.href"
            :data-ui="`rewards-card-help-${link.key}`"
            @click="handleLinkClick(link.tag)"
          >
            {{ link.label }}
          </CdrLink>
        </li>
      </ul>
    </div>
  </article>
</template>

<style lang="scss">
  @import 'src/main/style/rewardsCard.scss';
</style>
